import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DetailRow } from '../../ui/detail-card/payarc-detail-card.component';
import { DateonlyPipe } from '../../shared/custom-pipes/date-format-pipe/dateonly.pipe';

@Component({
    selector: 'app-api-token',
    templateUrl: './api-token.component.html',
    styleUrls: ['./api-token.component.css'],
    standalone: false
})
export class ApiTokenComponent {
  readonly detailRows: DetailRow[];
  copied = false;
  // The token is fetched after the panel opens (see ApiMainComponent.openRevealModal): show a
  // spinner while loading, an inline error if the reveal call failed, the token + copy once it lands.
  token: string | null;
  loading: boolean;
  error: string | null;
  // Copilot review (PDT-35833): a copy failure is kept separate from `error`. `error` gates the
  // token display (reveal failure = no token to show); a copy failure must leave the token visible
  // so the user can still select and copy it by hand.
  copyError: string | null = null;
  private copiedTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly dateonly = new DateonlyPipe();

  constructor(
    private dialog: MatDialogRef<ApiTokenComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { token?: string, item: any, loading?: boolean, error?: string }
  ) {
    this.token = data.token ?? null;
    this.loading = data.loading ?? false;
    this.error = data.error ?? null;
    this.detailRows = [
      { label: 'ID', value: data.item.id },
      { label: 'Client ID', value: data.item.client_id },
      { label: 'Client secret', value: data.item.client_secret || '--' },
      { label: 'Use count', value: data.item.use_count ?? '--' },
      { label: 'Last used', value: data.item.last_used ? this.dateonly.transform(data.item.last_used, 'MM/DD/yyyy') : '--' },
      { label: 'Expiry date', value: data.item.expires_at ? this.dateonly.transform(data.item.expires_at, 'MM/DD/yyyy') : '--' },
    ];
  }

  setToken(token: string): void {
    this.token = token;
    this.loading = false;
    this.error = null;
  }

  setError(message: string): void {
    this.error = message;
    this.loading = false;
  }

  closeAction() {
    this.dialog.close('cancel');
  }

  // Copy via the async Clipboard API, with the execCommand path as a fallback for older/insecure
  // contexts. Shows "Copied" for 2s then reverts (the old version wrote "Copied" onto the button
  // permanently and never reset).
  async copy(val: string) {
    if (!val) { return; }
    // Copilot review (PDT-35833): only show "Copied" when the copy actually succeeded. The async
    // Clipboard API throwing OR the legacy execCommand returning false both count as failure, so we
    // don't claim success when the token wasn't put on the clipboard (e.g. permission denied).
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(val);
        ok = true;
      } else {
        ok = this.legacyCopy(val);
      }
    } catch {
      ok = this.legacyCopy(val);
    }
    if (!ok) {
      // Keep the token visible (don't touch `error`) so it can be copied by hand.
      this.copyError = 'Could not copy automatically. Select the token above and copy it manually.';
      return;
    }
    this.copyError = null;
    this.copied = true;
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => (this.copied = false), 2000);
  }

  // Returns whether the copy actually happened (execCommand's boolean result), so copy() doesn't
  // report success on a browser where this fallback is unsupported or denied.
  private legacyCopy(val: string): boolean {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(selBox);
    return ok;
  }
}
